/**
 * A step selected on the canvas: its name (renaming rewrites every
 * reference to it), its capability, its merge mode when more than one link
 * comes in (D16), and where each of its input fields comes from.
 */
import { useEffect, useState } from 'react';

import type { LinkageJoin, LinkageMode } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Input, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select, type SelectOption } from '../../ui/Select.js';
import { followsCall, setCapability, setMerge, setSource, type Draft } from './draft.js';
import { InspectorPanel } from './InspectorPanel.js';
import { itemReferences, specAt } from './items.js';
import { modeOf, removeStepAndRetarget, setMode } from './modes.js';
import { MappingRow } from './MappingRow.js';
import { mappableFields, referencesFor, type Schemas } from './references.js';
import { renameRefusal, renameStep } from './rename.js';
import type { Selection } from './selection.js';
import { WriteBackEditor } from './WriteBackEditor.js';

// What each mode means, under its control (Feature 71 D39).

const AFTER_CALL_HINT =
  "After a call only independent is offered: its task runs beside the call, as the caller on the call's agent, and reaches the organization by webhook.";

const MODE_HINT: Record<LinkageMode, string> = {
  merged: 'its result is listed under linked on its root',
  write_back: 'writes fields of its result into a node before it',
  independent: 'its own task, delivered by webhook; the node before does not wait',
};

export function StepInspector({
  draft,
  name,
  capabilities,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  name: string;
  capabilities: readonly SelectOption[];
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  const step = draft.steps.find((s) => s.name === name);
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  if (!step) return null;

  const refusal = renameRefusal(draft, step.name, text);
  const incoming = draft.links.filter((l) => l.to === step.name).length;
  const afterCall = followsCall(draft, step.name);
  const schema = schemas.get(step.capability_id);
  const references = referencesFor(draft, { kind: 'step', name: step.name }, schemas);
  const commitName = () => {
    if (refusal !== null || text === step.name) return;
    onChange(renameStep(draft, step.name, text));
    onSelect({ kind: 'node', id: text });
  };

  return (
    <InspectorPanel>
      <Typography variant="eyebrow">Step</Typography>
      <Field id="step-name" label="Name" error={refusal ?? undefined}>
        <Input
          id="step-name"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitName();
          }}
        />
      </Field>
      <Field id="step-capability" label="Capability">
        <Select
          searchable
          id="step-capability"
          value={step.capability_id}
          onChange={(value) => onChange(setCapability(draft, step.name, value))}
          options={capabilities}
        />
      </Field>
      {incoming > 1 && (
        <Field
          id="step-merge"
          label="Runs when its links hold"
          hint="waits for every link in first"
        >
          <SegmentedControl
            items={[
              { value: 'all', label: 'All' },
              { value: 'any', label: 'Any' },
            ]}
            value={step.merge}
            onChange={(value) => onChange(setMerge(draft, step.name, value as LinkageJoin))}
          />
        </Field>
      )}
      <Field
        id="step-mode"
        label="Mode"
        hint={afterCall ? AFTER_CALL_HINT : MODE_HINT[modeOf(step)]}
      >
        <SegmentedControl
          items={
            // After a call only independent is offered (Feature 78 D2).
            afterCall
              ? [{ value: 'independent', label: 'Independent' }]
              : [
                  { value: 'merged', label: 'Merged' },
                  { value: 'write_back', label: 'Write-back' },
                  { value: 'independent', label: 'Independent' },
                ]
          }
          value={modeOf(step)}
          onChange={(value) => onChange(setMode(draft, step.name, value as LinkageMode))}
        />
      </Field>
      {modeOf(step) === 'write_back' && (
        <WriteBackEditor draft={draft} step={step} schemas={schemas} onChange={onChange} />
      )}
      <Typography variant="eyebrow">Input mapping</Typography>
      {schema === undefined ? (
        <Typography variant="meta">
          Its fields are not known: the capability is loading, or it is not installed here.
        </Typography>
      ) : mappableFields(schema).length === 0 ? (
        <Typography variant="meta">This capability takes no input to map.</Typography>
      ) : (
        mappableFields(schema).map((field) => (
          <MappingRow
            key={field.name}
            field={field}
            source={step.mapping[field.name]}
            references={references}
            itemsOf={(ref) =>
              itemReferences(
                specAt(draft, ref, schemas),
                references.find((r) => r.value === ref)?.name ?? ref,
              )
            }
            onChange={(source) => onChange(setSource(draft, step.name, field.name, source))}
          />
        ))
      )}
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          onChange(removeStepAndRetarget(draft, step.name));
          onSelect(null);
        }}
      >
        Remove step
      </Button>
    </InspectorPanel>
  );
}
